// ============================================================================
// components/block/liveStreamManagment/vuMeterZones.ts
//
// Zones de couleur des VU-mètres de la régie (2026-10-23), et leur unité.
//
// Deux modes, un seul réglage pour toute la régie (mémorisé dans ce navigateur) :
//   - 'peak' — dBFS crête, comme une table de mixage numérique : vert jusqu'à
//     −18 dBFS (niveau nominal), jaune jusqu'à −6, orange jusqu'à 0, rouge au-delà
//     (écrêtage) ;
//   - 'lufs' — loudness EBU R128 « Momentary » (400 ms), zones autour de la cible
//     streaming de −14 LUFS : vert jusqu'à cible +2, jaune jusqu'à +5, orange
//     jusqu'à +8, rouge au-delà.
//
// Les zones sont FIXES sur l'échelle : la barre les découvre en montant, comme une
// colonne de LED. Chaque VU garde sa propre échelle (linéaire en dB pour le deck,
// carrée pour le mixeur…) : il fournit sa conversion valeur → % et reçoit le
// dégradé correspondant. LUFS et dBFS partagent la même plage numérique (−60 → +6).
// ============================================================================

import { useSyncExternalStore } from 'react';

export type VuMeterMode = 'peak' | 'lufs';

/** Cible de loudness (LUFS) : celle des plateformes de streaming (YouTube, Spotify). */
export const LUFS_TARGET = -14;

type Zones = ReadonlyArray<{ upTo: number; color: string }>;

const PEAK_ZONES: Zones = [
  { upTo: -18,      color: '#22c55e' },   // vert   — niveau nominal
  { upTo:  -6,      color: '#eab308' },   // jaune  — fort
  { upTo:   0,      color: '#f97316' },   // orange — proche de la saturation
  { upTo: Infinity, color: '#ef4444' },   // rouge  — écrêtage
];

const LUFS_ZONES: Zones = [
  { upTo: LUFS_TARGET + 2, color: '#22c55e' },   // vert   — jusqu'à la cible (+2)
  { upTo: LUFS_TARGET + 5, color: '#eab308' },   // jaune  — un peu fort
  { upTo: LUFS_TARGET + 8, color: '#f97316' },   // orange — trop fort
  { upTo: Infinity,        color: '#ef4444' },   // rouge  — beaucoup trop fort
];

const zonesOf = (mode: VuMeterMode): Zones => (mode === 'lufs' ? LUFS_ZONES : PEAK_ZONES);

/** Unité affichée d'un mode. */
export const vuUnit = (mode: VuMeterMode): string => (mode === 'lufs' ? 'LUFS' : 'dBFS');

/** Couleur de la zone où tombe une valeur, pour un voyant ou un chiffre. */
export function vuColorForDb(value: number, mode: VuMeterMode = 'peak'): string {
  const zones = zonesOf(mode);
  return (zones.find(z => value <= z.upTo) ?? zones[zones.length - 1]).color;
}

/**
 * Dégradé à arrêts francs des zones, le long de `direction` ('to top' pour une voie
 * verticale, 'to right' pour une barre horizontale), d'après l'échelle du VU.
 */
export function vuGradient(direction: 'to top' | 'to right', toPct: (value: number) => number, mode: VuMeterMode = 'peak'): string {
  const stops: string[] = [];
  let from = 0;
  for (const z of zonesOf(mode)) {
    const to = z.upTo === Infinity ? 100 : Math.max(from, Math.min(100, toPct(z.upTo)));
    stops.push(`${z.color} ${from.toFixed(2)}%`, `${z.color} ${to.toFixed(2)}%`);
    from = to;
  }
  return `linear-gradient(${direction}, ${stops.join(', ')})`;
}

/**
 * Fines séparations entre segments, posées par-dessus la barre : l'aspect d'une
 * colonne de LED. `pitch` = pas d'un segment en px, dont 1 px d'interstice.
 */
export function vuSegments(direction: 'to top' | 'to right', pitch = 4): string {
  return `repeating-linear-gradient(${direction}, transparent 0 ${pitch - 1}px, rgba(0,0,0,0.55) ${pitch - 1}px ${pitch}px)`;
}

// ── Mode de la régie ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'regie.vuMeterMode';

function readStoredMode(): VuMeterMode {
  try { return window.localStorage.getItem(STORAGE_KEY) === 'lufs' ? 'lufs' : 'peak'; }
  catch { return 'peak'; }
}

let currentMode: VuMeterMode = typeof window !== 'undefined' ? readStoredMode() : 'peak';
const listeners = new Set<() => void>();

/** Mode en cours, lisible hors React (boucles de mesure). */
export const getVuMeterMode = (): VuMeterMode => currentMode;

export function setVuMeterMode(mode: VuMeterMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* navigation privée */ }
  listeners.forEach(fn => fn());
}

export function subscribeVuMeterMode(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Mode en cours pour un composant : re-rend au changement (rare), jamais au rythme du son. */
export function useVuMeterMode(): VuMeterMode {
  return useSyncExternalStore(subscribeVuMeterMode, getVuMeterMode, getVuMeterMode);
}
